/**
 * Sprint-104d-full — PaymentOverviewCard.
 *
 * Surfaces the payment block on the customer tracking page:
 *   • method (COD / Wallet / Online)
 *   • status (paid / pending / settled)
 *   • amount paid + amount due (if a COD or pending settlement)
 *   • a "Download invoice" button hitting `/api/orders/{tid}/invoice.pdf`
 *
 * The amount math is defensive: orders predating Sprint-90 carry a flat
 * `total_amount`, while newer ones carry a richer `price` dict (with
 * subtotal/gst/total/cod_fee). We pick whichever's there.
 */
import { useState } from "react";
import { CreditCard, Wallet, IndianRupee, Download, Loader2, ShieldCheck, CircleAlert } from "lucide-react";
import api from "../../services/api";
import { toast } from "sonner";

function paymentSummary(order) {
  if (!order) return {};
  const priceObj = (order.price && typeof order.price === "object") ? order.price : null;
  const total = priceObj?.total ?? priceObj?.final_price ?? order.total_amount ?? null;
  const subtotal = priceObj?.subtotal ?? priceObj?.base_total ?? order.subtotal ?? null;
  const gst = priceObj?.gst ?? priceObj?.tax_amount ?? null;
  const codFee = priceObj?.cod_fee ?? null;
  const walletUsed = order.wallet_used ?? order.wallet_amount ?? 0;
  const amountPaid = order.amount_paid ?? (order.payment_status === "paid" ? total : (walletUsed || 0));
  // Outstanding can come from Sprint-80 pending_settlements OR a COD that
  // hasn't been marked paid yet.
  const pendingSettle = Array.isArray(order.pending_settlements) ? order.pending_settlements : [];
  const pendingFromEdits = pendingSettle
    .filter((p) => p?.status === "pending")
    .reduce((s, p) => s + Number(p?.amount || 0), 0);
  const codOutstanding =
    (order.payment_method || "").toLowerCase() === "cod" && order.payment_status !== "paid"
      ? Number(total || 0)
      : 0;
  const amountDue = (pendingFromEdits || 0) + (codOutstanding || 0);
  return { total, subtotal, gst, codFee, walletUsed, amountPaid, amountDue };
}

export default function PaymentOverviewCard({ order, trackingId }) {
  const [downloading, setDownloading] = useState(false);
  if (!order) return null;
  const s = paymentSummary(order);
  const methodKey = (order.payment_method || "—").toLowerCase();
  const methodLabel = methodKey === "cod" ? "Cash on delivery"
                     : methodKey === "wallet" ? "PickNGo wallet"
                     : methodKey === "razorpay" || methodKey === "online" ? "Online (Razorpay)"
                     : methodKey.toUpperCase();
  const Icon = methodKey === "wallet" ? Wallet : CreditCard;
  const paid = order.payment_status === "paid" || methodKey === "wallet" || !!order.paid_at;
  const codPending = methodKey === "cod" && !paid && (order.status !== "cancelled");

  const downloadInvoice = async () => {
    setDownloading(true);
    try {
      // Use api so the auth header travels. responseType=blob to capture
      // the PDF bytes; fall back to JSON if the server returned that.
      const resp = await api.get(`/orders/${trackingId}/invoice.pdf`, { responseType: "blob" });
      const blob = resp.data;
      const isJSON = (blob.type || "").startsWith("application/json");
      const ext = isJSON ? "json" : "pdf";
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PickNGo-invoice-${trackingId}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Invoice downloaded (${ext.toUpperCase()})`);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 403) toast.error("You don't have access to this invoice");
      else if (code === 404) toast.error("Invoice not found for this order");
      else toast.error("Couldn't download invoice — try again");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 sm:p-6" data-testid="payment-overview-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-['Outfit'] text-base font-bold text-slate-900 flex items-center gap-2">
          <Icon className="h-4 w-4 text-[#FF6B00]" /> Payment
        </h2>
        <span
          className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${
            paid ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-amber-50 text-amber-700 border border-amber-200"
          }`}
          data-testid="payment-status-badge"
        >
          {paid ? <><ShieldCheck className="h-3 w-3" /> Paid</> : <><CircleAlert className="h-3 w-3" /> Pending</>}
        </span>
      </div>

      <dl className="text-sm space-y-1.5">
        <div className="flex items-center justify-between">
          <dt className="text-slate-500">Method</dt>
          <dd className="font-semibold text-slate-800" data-testid="payment-method-text">{methodLabel}</dd>
        </div>
        {s.subtotal != null && (
          <div className="flex items-center justify-between">
            <dt className="text-slate-500">Subtotal</dt>
            <dd className="tabular-nums text-slate-700">₹{s.subtotal}</dd>
          </div>
        )}
        {s.gst != null && (
          <div className="flex items-center justify-between">
            <dt className="text-slate-500">GST</dt>
            <dd className="tabular-nums text-slate-700">₹{s.gst}</dd>
          </div>
        )}
        {s.codFee != null && s.codFee > 0 && (
          <div className="flex items-center justify-between">
            <dt className="text-slate-500">COD fee</dt>
            <dd className="tabular-nums text-slate-700">₹{s.codFee}</dd>
          </div>
        )}
        {s.walletUsed > 0 && (
          <div className="flex items-center justify-between">
            <dt className="text-slate-500">Wallet applied</dt>
            <dd className="tabular-nums text-emerald-700">− ₹{s.walletUsed}</dd>
          </div>
        )}
        <div className="flex items-center justify-between pt-2 mt-1 border-t border-slate-100">
          <dt className="font-semibold text-slate-900">Total</dt>
          <dd className="font-bold text-lg text-slate-900 tabular-nums inline-flex items-center" data-testid="payment-total-text">
            <IndianRupee className="h-4 w-4" />{s.total ?? "—"}
          </dd>
        </div>
        {s.amountPaid != null && Number(s.amountPaid) > 0 && (
          <div className="flex items-center justify-between text-xs">
            <dt className="text-slate-500">Amount paid</dt>
            <dd className="tabular-nums text-emerald-700">₹{s.amountPaid}</dd>
          </div>
        )}
        {s.amountDue > 0 && (
          <div className="flex items-center justify-between text-xs">
            <dt className="text-amber-700 font-semibold">Amount due</dt>
            <dd className="tabular-nums text-amber-800 font-semibold" data-testid="payment-amount-due">₹{s.amountDue}</dd>
          </div>
        )}
        {codPending && (
          <div className="mt-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800" data-testid="payment-cod-pending-banner">
            <b>COD pending</b> — pay <span className="tabular-nums">₹{s.total}</span> to the delivery agent on handover.
          </div>
        )}
      </dl>

      <div className="mt-4 flex items-center justify-end">
        <button
          type="button"
          onClick={downloadInvoice}
          disabled={downloading}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed transition"
          data-testid="payment-download-invoice-btn"
        >
          {downloading
            ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…</>
            : <><Download className="h-3.5 w-3.5" /> Download invoice</>}
        </button>
      </div>
    </div>
  );
}
